import { View, ScrollView, Text, TouchableOpacity } from 'react-native'
import KPICard from '@/components/Charts/KPICard'
import { StyleSheet } from 'react-native-unistyles';
import { Query } from '@/utils/react-query/Queries';
import { QueryKeys } from '@/utils/react-query/QueryKeys';
import { DateTime } from 'luxon';
import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import DatePickerInput from '@/components/General/inputs/DatePickerInput';
import Chart from '@/components/Charts/Chart';
import usePlatform from '@/utils/hooks/usePlatform';
import { queryClient } from '@/data/client';
import { getAttendanceDateFieldsKPIs, getAttendanceUniqueDetail, getAttendanceFirstFouthTimeVisitorsDetail } from '@/data/reports';
import { ActivityIndicator } from 'react-native-paper';
import { useLocalSearchParams, useRouter, usePathname, useNavigation } from 'expo-router';
import CutCard from '@/components/General/CutCard';
import Unicon from '@/components/General/Unicon';
import SaveShortcutModal from '@/components/General/SaveShortcutModal';
import DropdownMenu from '@/components/General/DropdownMenu';
import { RefreshControl } from 'react-native-gesture-handler';
import { dayContainer } from '@/utils/reportDates';

const fieldsToFetch = ['Water Baptism', 'Spirit Baptism']

const DATE_PRESETS = [
    { label: 'This Week', value: 'this_week' },
    { label: 'Last Week', value: 'last_week' },
    { label: 'This Month', value: 'this_month' },
    { label: 'Month to Date', value: 'month_to_date' },
    { label: 'Last Month', value: 'last_month' },
    { label: 'This Quarter', value: 'this_quarter' },
    { label: 'Last Quarter', value: 'last_quarter' },
    { label: 'This Year', value: 'this_year' },
    { label: 'Year to Date', value: 'year_to_date' },
    { label: 'Last Year', value: 'last_year' },
    { label: 'Past 30 Days', value: 'last_30_days' },
    { label: 'Past 90 Days', value: 'last_90_days' },
    { label: 'Past 12 Months', value: 'last_12_months' },
];

// Dates are day containers: device-local midnight of the org-local calendar day.
const getPresetDates = (preset, timeZone) => {
    const now = timeZone ? DateTime.now().setZone(timeZone) : DateTime.now();
    switch (preset) {
        case 'month_to_date':
            return { start: dayContainer(now.startOf('month')), end: dayContainer(now) };
        case 'year_to_date':
            return { start: dayContainer(now.startOf('year')), end: dayContainer(now) };
        case 'this_week':
            return { start: dayContainer(now.startOf('week')), end: dayContainer(now.endOf('week')) };
        case 'last_week':
            return { start: dayContainer(now.minus({ weeks: 1 }).startOf('week')), end: dayContainer(now.minus({ weeks: 1 }).endOf('week')) };
        case 'this_month':
            return { start: dayContainer(now.startOf('month')), end: dayContainer(now.endOf('month')) };
        case 'last_month':
            return { start: dayContainer(now.minus({ months: 1 }).startOf('month')), end: dayContainer(now.minus({ months: 1 }).endOf('month')) };
        case 'this_quarter':
            return { start: dayContainer(now.startOf('quarter')), end: dayContainer(now.endOf('quarter')) };
        case 'last_quarter':
            return { start: dayContainer(now.minus({ quarters: 1 }).startOf('quarter')), end: dayContainer(now.minus({ quarters: 1 }).endOf('quarter')) };
        case 'this_year':
            return { start: dayContainer(now.startOf('year')), end: dayContainer(now.endOf('year')) };
        case 'last_year':
            return { start: dayContainer(now.minus({ years: 1 }).startOf('year')), end: dayContainer(now.minus({ years: 1 }).endOf('year')) };
        case 'last_30_days':
            return { start: dayContainer(now.minus({ days: 30 })), end: dayContainer(now) };
        case 'last_90_days':
            return { start: dayContainer(now.minus({ days: 90 })), end: dayContainer(now) };
        case 'last_12_months':
            return { start: dayContainer(now.minus({ months: 12 })), end: dayContainer(now) };
        default:
            return null;
    }
};

const SectionHeader = ({ title, subtitle }) => (
    <View style={styles.sectionHeader}>
        <View style={styles.sectionAccent} />
        <View style={styles.sectionHeaderText}>
            <Text style={styles.sectionTitle}>{title}</Text>
            {subtitle ? <Text style={styles.sectionSubtitle}>{subtitle}</Text> : null}
        </View>
    </View>
);

const GroupLabel = ({ children }) => (
    <Text style={styles.groupLabel}>{children}</Text>
);

export default function AttendanceDashboardV2() {
    const router = useRouter();
    const pathname = usePathname();
    const params = useLocalSearchParams();
    const navigation = useNavigation();

    const weeklyAttendanceChartRef = useRef(null);
    const returnLiklihoodChartRef = useRef(null);
    const uniqueAttendeesChartRef = useRef(null);
    const growthChartRef = useRef(null);

    const [initialized, setInitialized] = useState(false);
    const [startDate, setStartDate] = useState(null);
    const [endDate, setEndDate] = useState(null);
    const [selectedPreset, setSelectedPreset] = useState('month_to_date');
    const [fieldsToShow, setFieldsToShow] = useState([]);
    const [shortcutModalOpen, setShortcutModalOpen] = useState(false);
    const [uniqueAttendanceDetail, setUniqueAttendanceDetail] = useState(null);
    const [uniqueAttendanceDetailLoading, setUniqueAttendanceDetailLoading] = useState(false);
    const [visitorsDetail, setVisitorsDetail] = useState(null);
    const [visitorsDetailLoading, setVisitorsDetailLoading] = useState(false);

    const { isWeb, isNative } = usePlatform();
    const { data: loggedInUser } = Query.people.loggedInUser();
    const { data: organization } = Query.organization.byId({ id: loggedInUser?.organizationID });
    const orgTimezone = organization?.timezone;

    // Initialize state from URL params on mount
    useEffect(() => {
        if (initialized || !orgTimezone) return;

        const { preset, startDate: urlStart, endDate: urlEnd } = params;

        if (preset && preset !== 'custom') {
            const presetDates = getPresetDates(preset, orgTimezone);
            if (presetDates) {
                setSelectedPreset(preset);
                setStartDate(presetDates.start);
                setEndDate(presetDates.end);
            } else {
                const defaultDates = getPresetDates('month_to_date', orgTimezone);
                setStartDate(defaultDates.start);
                setEndDate(defaultDates.end);
            }
        } else if (urlStart && urlEnd) {
            setSelectedPreset('custom');
            setStartDate(DateTime.fromISO(urlStart).toJSDate());
            setEndDate(DateTime.fromISO(urlEnd).toJSDate());
        } else {
            const defaultDates = getPresetDates('month_to_date', orgTimezone);
            setStartDate(defaultDates.start);
            setEndDate(defaultDates.end);
        }

        setInitialized(true);
    }, [params, initialized, orgTimezone]);

    // Update URL when dates change
    useEffect(() => {
        if (!initialized || !startDate || !endDate) return;

        const newParams = {};
        if (selectedPreset !== 'custom') {
            newParams.preset = selectedPreset;
        } else {
            newParams.startDate = DateTime.fromJSDate(startDate).toISODate();
            newParams.endDate = DateTime.fromJSDate(endDate).toISODate();
        }

        router.setParams(newParams);
    }, [startDate, endDate, selectedPreset, initialized]);

    const handlePresetChange = (preset) => {
        setSelectedPreset(preset);
        if (preset !== 'custom') {
            const presetDates = getPresetDates(preset, orgTimezone);
            if (presetDates) {
                setStartDate(presetDates.start);
                setEndDate(presetDates.end);
            }
        }
    };

    const handleStartDateChange = (date) => {
        setSelectedPreset('custom');
        setStartDate(date);
    };

    const handleEndDateChange = (date) => {
        setSelectedPreset('custom');
        setEndDate(date);
    };

    const currentUrl = useMemo(() => {
        if (!startDate || !endDate) return pathname;
        const searchParams = new URLSearchParams();
        if (selectedPreset && selectedPreset !== 'custom') {
            searchParams.set('preset', selectedPreset);
        } else {
            searchParams.set('startDate', DateTime.fromJSDate(startDate).toISODate());
            searchParams.set('endDate', DateTime.fromJSDate(endDate).toISODate());
        }
        return `${pathname}?${searchParams.toString()}`;
    }, [pathname, startDate, endDate, selectedPreset]);

    useEffect(() => {
        const menuOptions = [
            { name: 'Save as Shortcut', onSelect: () => setShortcutModalOpen(true) }
        ];

        navigation.setOptions({
            headerRight: () => <DropdownMenu options={menuOptions} />
        });
    }, [navigation, currentUrl]);

    const {data: personCustomFields} = Query.personCustomFields.all()
    const {data: attendanceUnique, isLoading: attendanceUniqueLoading} = Query.kpis.attendanceUnique({startDate, endDate, options: { enabled: !!startDate && !!endDate }});
    const {data: attendanceWeeklyUniqueAvg, isLoading: attendanceWeeklyUniqueAvgLoading} = Query.kpis.attendanceWeeklyUniqueAvg({startDate, endDate});
    const {data: attendanceFirstFouthTimeVisitors} = Query.kpis.attendanceFirstFouthTimeVisitors({startDate, endDate, options: { enabled: !!startDate && !!endDate }});

    useEffect(() => {
        setUniqueAttendanceDetail(null);
        setVisitorsDetail(null);
    }, [startDate, endDate]);

    useEffect(() => {
        if (personCustomFields && startDate && endDate) {
            const dateFields = personCustomFields.filter((field) => field.type === 'Date');
            setFieldsToShow([])

            for(const field of dateFields){
                if(fieldsToFetch.includes(field.name)){
                    Promise.all([
                        handleDateQuery(field, true),   // count only
                        handleDateQuery(field, false)   // full details
                    ]).then(([countData, detailsData]) => {
                        setFieldsToShow((prev) => [...prev, {
                            id: field.id,
                            name: field.name,
                            count: countData.count,
                            values: detailsData.values
                        }])
                    })
                }
            }
        }
    }, [personCustomFields, startDate, endDate])

    const handleDateQuery = useCallback(async (field, countOnly) => {
        const attendanceDateFields = await queryClient.fetchQuery({
            queryKey: QueryKeys.kpis.attendanceDateFields({startDate, endDate, dateFieldID: field.id, countOnly}),
            queryFn: () => getAttendanceDateFieldsKPIs(startDate, endDate, field.id, countOnly),
            staleTime: Infinity
        })

        return {id: field.id, name: field.name, ...attendanceDateFields}
    }, [startDate, endDate])

    const handleUniqueAttendanceOpen = useCallback(async () => {
        if (uniqueAttendanceDetail || !startDate || !endDate) return;
        setUniqueAttendanceDetailLoading(true);
        try {
            const data = await queryClient.fetchQuery({
                queryKey: QueryKeys.kpis.attendanceUniqueDetail({startDate, endDate}),
                queryFn: () => getAttendanceUniqueDetail(startDate, endDate),
                staleTime: Infinity
            });
            setUniqueAttendanceDetail(data);
        } finally {
            setUniqueAttendanceDetailLoading(false);
        }
    }, [startDate, endDate, uniqueAttendanceDetail]);

    const handleVisitorsDetailOpen = useCallback(async (_visitNumber) => {
        if (visitorsDetail || !startDate || !endDate) return;
        setVisitorsDetailLoading(true);
        try {
            const data = await queryClient.fetchQuery({
                queryKey: QueryKeys.kpis.attendanceFirstFouthTimeVisitorsDetail({startDate, endDate}),
                queryFn: () => getAttendanceFirstFouthTimeVisitorsDetail(startDate, endDate),
                staleTime: Infinity
            });
            setVisitorsDetail(data);
        } finally {
            setVisitorsDetailLoading(false);
        }
    }, [startDate, endDate, visitorsDetail]);

    const getOrdinal = (num) => {
        if (num === 1) return '1st';
        if (num === 2) return '2nd';
        if (num === 3) return '3rd';
        return num + 'th';
    }

    const refreshList = () => {
        queryClient.invalidateQueries({queryKey: ['attendanceUniqueKPIs']})
        queryClient.invalidateQueries({queryKey: ['attendanceWeeklyUniqueAvgKPIs']})
        queryClient.invalidateQueries({queryKey: ['attendanceFirstFouthTimeVisitorsKPIs']})
        setUniqueAttendanceDetail(null);
        setVisitorsDetail(null);
        weeklyAttendanceChartRef.current?.reload();
        returnLiklihoodChartRef.current?.reload();
        uniqueAttendeesChartRef.current?.reload();
        growthChartRef.current?.reload();
    }

    const rangeLabel = useMemo(() => {
        if (selectedPreset && selectedPreset !== 'custom') {
            return DATE_PRESETS.find(d => d.value === selectedPreset)?.label ?? 'Custom range';
        }
        if (startDate && endDate) {
            return `${DateTime.fromJSDate(startDate).toFormat('MMM d, yyyy')} – ${DateTime.fromJSDate(endDate).toFormat('MMM d, yyyy')}`;
        }
        return '';
    }, [selectedPreset, startDate, endDate]);

    if (!initialized || !startDate || !endDate) {
        return (
            <View style={styles.loadingContainer}>
                <ActivityIndicator />
            </View>
        );
    }

    if(!loggedInUser?.can?.('Check In')){
        return <View style={styles.permissionContainer}>
            <Text style={styles.permissionText}>You do not have permission to view this page.</Text>
        </View>
    }

    return (
        <>
        <ScrollView contentContainerStyle={styles.screen} keyboardShouldPersistTaps="handled" refreshControl={<RefreshControl refreshing={false} onRefresh={refreshList}/>}>

            <View style={styles.hero}>
                <View style={styles.heroIconContainer}>
                    <Unicon name="calendar-outline" library="ionicon" colorVariant="onPrimary" size={28} />
                </View>
                <Text style={styles.heroTitle}>Attendance Dashboard</Text>
                <Text style={styles.heroDescription}>
                    Track attendance trends, event totals, and visitor patterns.
                </Text>
            </View>

            {/* ===== SECTION 1: Date-range dependent ===== */}
            <SectionHeader title="By Date Range" subtitle="Metrics for the selected period" />

            {/* ===== Control panel: date range selection ===== */}
            <CutCard accentColor="primary" style={styles.controlPanel} contentStyle={styles.controlPanelContent}>
                <View style={styles.controlPanelHeader}>
                    <Text style={styles.controlPanelLabel}>Date Range</Text>
                    <Text style={styles.controlPanelRange}>{rangeLabel}</Text>
                </View>

                <ScrollView
                    horizontal
                    showsHorizontalScrollIndicator={false}
                    contentContainerStyle={styles.presetsContainer}
                    style={isNative ? styles.presetsScrollNative : styles.presetsScrollWeb}
                    keyboardShouldPersistTaps="handled"
                >
                    {DATE_PRESETS.map((preset) => (
                        <TouchableOpacity
                            key={preset.value}
                            style={[
                                styles.presetPill,
                                selectedPreset === preset.value && styles.presetPillActive
                            ]}
                            onPress={() => handlePresetChange(preset.value)}
                        >
                            <Text style={[
                                styles.presetPillText,
                                selectedPreset === preset.value && styles.presetPillTextActive
                            ]}>
                                {preset.label}
                            </Text>
                        </TouchableOpacity>
                    ))}
                </ScrollView>

                <View style={styles.filtersRow}>
                    <View style={styles.filterItem}>
                        <DatePickerInput maximumDate={endDate} title="Start Date" value={startDate} onUpdate={handleStartDateChange} hideClearIcon />
                    </View>
                    <View style={styles.filterItem}>
                        <DatePickerInput minimumDate={startDate} maximumDate={new Date()} title="End Date" value={endDate} onUpdate={handleEndDateChange} hideClearIcon />
                    </View>
                </View>
            </CutCard>

            <GroupLabel>Attendance</GroupLabel>
            <View style={styles.kpiGrid}>
                <KPICard
                    title="Unique Attendance"
                    content={attendanceUniqueLoading ? <ActivityIndicator /> : attendanceUnique ?? 0}
                    onDetailsOpen={handleUniqueAttendanceOpen}
                    detailsLoading={uniqueAttendanceDetailLoading}
                    detailsTitle="Breakdown"
                    details={uniqueAttendanceDetail ? {
                        columns: [
                            { key: 'name', label: 'Name', width: 150, link: (row) => row.person_id ? `/people/${row.person_id}` : null },
                            { key: 'times_attended', label: 'Times', width: 70 },
                        ],
                        data: uniqueAttendanceDetail.map(r => ({
                            person_id: r.person_id,
                            name: `${r.first_name || ''} ${r.last_name || ''}`.trim(),
                            first_name: r.first_name,
                            last_name: r.last_name,
                            email: r.email,
                            phones: r.phones,
                            times_attended: r.times_attended
                        }))
                    } : null}
                />
                <KPICard
                    title="Weekly Unique Avg"
                    content={attendanceWeeklyUniqueAvgLoading ? <ActivityIndicator /> : attendanceWeeklyUniqueAvg ?? 0}
                />
            </View>

            <GroupLabel>Milestones</GroupLabel>
            <View style={styles.kpiGrid}>
                {fieldsToFetch.map((field) => {
                    const fieldData = fieldsToShow.find((item) => item.name === field);
                    const transformedValues = fieldData?.values?.map(v => {
                        let formattedDate = v.field_value;
                        if (v.field_value) {
                            if (v.field_value.includes('T') || v.field_value.includes('-')) {
                                formattedDate = DateTime.fromISO(v.field_value).toFormat('MM/dd/yyyy');
                            } else if (v.field_value.includes('/')) {
                                const parts = v.field_value.split('/');
                                if (parts.length === 3) {
                                    formattedDate = `${parts[0]}/${parts[1]}/${parts[2]}`;
                                }
                            }
                        }
                        return {
                            person_id: v.person_id,
                            name: `${v.first_name || ''} ${v.last_name || ''}`.trim(),
                            first_name: v.first_name,
                            last_name: v.last_name,
                            email: v.email,
                            phones: v.phones,
                            field_value: formattedDate
                        };
                    }) || [];
                    return (
                        <KPICard
                            key={field}
                            title={field}
                            content={fieldData?.count ?? <ActivityIndicator />}
                            details={transformedValues.length > 0 ? {
                                columns: [
                                    { key: 'name', label: 'Name', width: 150, link: (row) => row.person_id ? `/people/${row.person_id}` : null },
                                    { key: 'field_value', label: 'Date', width: 100 },
                                ],
                                data: transformedValues
                            } : null}
                            detailsTitle="Breakdown"
                        />
                    );
                })}
            </View>

            <GroupLabel>First-Time Guests</GroupLabel>
            <View style={styles.kpiGrid}>
                {attendanceFirstFouthTimeVisitors?.filter((item) => item.visit_number > 0).map((item, i) => {
                    const visitorRows = visitorsDetail
                        ?.filter(r => r.visit_number === item.visit_number)
                        .map(r => ({
                            person_id: r.person_id,
                            name: `${r.first_name || ''} ${r.last_name || ''}`.trim(),
                            first_name: r.first_name,
                            last_name: r.last_name,
                            email: r.email,
                            phones: r.phones,
                            visit_date: r.visit_date ? DateTime.fromISO(r.visit_date).toFormat('MM/dd/yyyy') : ''
                        })) || [];
                    return (
                        <KPICard
                            key={i}
                            title={`${getOrdinal(item.visit_number)} Time Guest`}
                            content={item.visitor_count}
                            onDetailsOpen={handleVisitorsDetailOpen}
                            detailsLoading={visitorsDetailLoading}
                            detailsTitle="Breakdown"
                            details={visitorsDetail ? {
                                columns: [
                                    { key: 'name', label: 'Name', width: 150, link: (row) => row.person_id ? `/people/${row.person_id}` : null },
                                    { key: 'visit_date', label: 'Date', width: 100 },
                                ],
                                data: visitorRows
                            } : null}
                        />
                    );
                })}
            </View>

            <GroupLabel>Charts</GroupLabel>
            <View style={styles.chartGrid}>
                <Chart ref={weeklyAttendanceChartRef} height={isWeb ? 500 : 400} chartName="WeeklyUniqueAttendanceChart" parameters={{startDate: DateTime.fromJSDate(startDate).toISODate(), endDate: DateTime.fromJSDate(endDate).toISODate()}} />
                <Chart ref={uniqueAttendeesChartRef} height={isWeb ? 500 : 400} chartName="AttendancePerEventChart" parameters={{startDate: DateTime.fromJSDate(startDate).toISODate(), endDate: DateTime.fromJSDate(endDate).toISODate()}} />
            </View>

            {/* ===== SECTION 2: Trends (independent of date range) ===== */}
            <SectionHeader title="Trends & Patterns" subtitle="Overall trends, independent of the selected range" />

            <View style={styles.chartGrid}>
                <Chart ref={growthChartRef} height={isWeb ? 620 : 520} chartName="AttendanceGrowthChart" parameters={{}} />
                <Chart ref={returnLiklihoodChartRef} height={isWeb ? 500 : 400} chartName="ReturnLikelihoodChart" />
            </View>
        </ScrollView>

            <SaveShortcutModal
                isOpen={shortcutModalOpen}
                onClose={() => setShortcutModalOpen(false)}
                personID={loggedInUser?.id}
                screen={currentUrl}
                icon="bar-chart-outline"
                library="ionicon"
                label={selectedPreset
                    ? `${DATE_PRESETS.find(d => d.value == selectedPreset)?.label} - Attendance`
                    : `${DateTime.fromJSDate(startDate).toFormat("MMM dd, yyyy")} to ${DateTime.fromJSDate(endDate).toFormat("MMM dd, yyyy")} - Attendance`}
            />
        </>
    )
}

const styles = StyleSheet.create(({colors, fonts, shadows}, {isPortrait}) => ({
    screen: {
        padding: 16,
        gap: 14,
    },
    loadingContainer: {
        flex: 1,
        justifyContent: 'center',
        alignItems: 'center',
    },
    permissionContainer: {
        flex: 1,
        padding: 24,
        justifyContent: 'center',
        alignItems: 'center',
    },
    permissionText: {
        fontSize: 16,
        fontFamily: fonts.body,
        color: colors.textSecondary,
        textAlign: 'center',
    },

    // Hero
    hero: {
        alignItems: 'center',
        paddingVertical: 20,
        gap: 12,
    },
    heroIconContainer: {
        width: 56,
        height: 56,
        borderRadius: 16,
        backgroundColor: colors.primary,
        alignItems: 'center',
        justifyContent: 'center',
    },
    heroTitle: {
        fontSize: 24,
        fontFamily: fonts.heading,
        color: colors.text,
        textAlign: 'center',
    },
    heroDescription: {
        fontSize: 14,
        fontFamily: fonts.body,
        color: colors.textSecondary,
        textAlign: 'center',
        maxWidth: 500,
        lineHeight: 20,
    },

    // Control panel — CutCard owns surface/radius; the shadow stays here so
    // it converts to a clip-path drop-shadow on web.
    controlPanel: {
        padding: 16,
        boxShadow: shadows.card,
        _web: {
            willChange: 'transform',
        },
    },
    controlPanelContent: {
        gap: 14,
    },
    controlPanelHeader: {
        flexDirection: 'row',
        alignItems: 'center',
        justifyContent: 'space-between',
        flexWrap: 'wrap',
        gap: 6,
    },
    controlPanelLabel: {
        fontSize: 12,
        fontFamily: fonts.heading,
        color: colors.textSecondary,
        textTransform: 'uppercase',
        letterSpacing: 0.6,
    },
    controlPanelRange: {
        fontSize: 14,
        fontFamily: fonts.bold,
        color: colors.primary,
    },
    presetsScrollNative: {
        flexGrow: 0,
        marginHorizontal: -16,
        paddingHorizontal: 16,
    },
    presetsScrollWeb: {
        flexGrow: 0,
    },
    presetsContainer: {
        flexDirection: 'row',
        gap: 8,
        paddingVertical: 2,
    },
    presetPill: {
        paddingHorizontal: 14,
        paddingVertical: 8,
        borderRadius: 20,
        backgroundColor: colors.background,
        _web:{
            transition: 'background-color 0.2s ease-in-out',
            _hover:{
                backgroundColor: colors.hoverStrong,
            }
        }
    },
    presetPillActive: {
        backgroundColor: colors.primary,
        _web:{
            transition: 'background-color 0.2s ease-in-out',
            _hover:{
                backgroundColor: colors.primary,
            }
        }
    },
    presetPillText: {
        fontSize: 13,
        fontFamily: fonts.body,
        color: colors.text,
    },
    presetPillTextActive: {
        color: colors.onPrimary,
        fontFamily: fonts.heading,
    },
    filtersRow: {
        flexDirection: 'row',
        flexWrap: 'wrap',
        gap: 12,
        alignItems: 'flex-end',
    },
    filterItem: {
        flexGrow: isPortrait ? 1 : 0,
    },

    // Section headers
    sectionHeader: {
        flexDirection: 'row',
        alignItems: 'center',
        gap: 10,
        marginTop: 10,
    },
    sectionAccent: {
        width: 4,
        alignSelf: 'stretch',
        minHeight: 30,
        borderRadius: 4,
        backgroundColor: colors.primary,
    },
    sectionHeaderText: {
        gap: 2,
    },
    sectionTitle: {
        fontSize: 19,
        fontFamily: fonts.bold,
        color: colors.text,
    },
    sectionSubtitle: {
        fontSize: 13,
        fontFamily: fonts.body,
        color: colors.textSecondary,
    },
    groupLabel: {
        fontSize: 12,
        fontFamily: fonts.heading,
        color: colors.textSecondary,
        textTransform: 'uppercase',
        letterSpacing: 0.6,
        marginTop: 4,
    },

    // Grids
    kpiGrid: {
        flexDirection: 'row',
        flexWrap: 'wrap',
        gap: 12,
    },
    chartGrid: {
        flexDirection: 'row',
        flexWrap: 'wrap',
        gap: 12,
    },
}));
